<template>
  <span>
    <a-form-model-item :labelCol="{span: 20, offset: 2}" v-if="!appointmentTypeId || !resourceId"
                       label="Selecteer eerst een type afspraak en resource." />

    <a-form-model-item label="Datum">
        <date-picker-component :value="bookableStartTime" :block="true" :allow-clear="true"
                               :format="dateFormat"
                               :disabled="loading || !appointmentTypeId || !resourceId"
                               :disabled-date="getDisabledDate"
                               @change="onChangeStartDate($event)" />
    </a-form-model-item>

    <a-form-model-item v-if="loading" label=" ">
      <strong>De beschikbare tijden worden geladen...</strong>
    </a-form-model-item>

    <a-form-model-item v-if="shouldHaveBookableTimes && doesntHaveBookableTimes && !loading" label=" ">
        <span style="font-weight: 400;color: #ff6400;">Komende week geen boekbare tijden beschikbaar</span>
    </a-form-model-item>

    <a-form-model-item label="Van">
      <template v-if="loading">
        <Skeleton class="skeleton" :loading="true"></Skeleton>
      </template>
      <template v-else>
        <a-select v-model="bookableStartTime" placeholder="Selecteer een tijd"
                  @change="onChangeStartTime($event)"
                  :disabled="!appointmentTypeId || !resourceId || (shouldHaveBookableTimes && doesntHaveBookableTimes && !loading)">
          <a-select-option v-if="groupedBookableTimes.length === 0" :value="bookableStartTime"
                           :key="bookableStartTime">
              Selecteer eerst een afspraaktype
          </a-select-option>
          <a-select-option v-else :value="bookableStartTime"
                           :key="bookableStartTime" :disabled="true">
              Selecteer een starttijd
          </a-select-option>
          <a-select-opt-group v-if="initialStartTime" label="Oorspronkelijke afspraaktijd">
            <a-select-option key="current" :value="initialStartTime">
              {{ makeMomentObject(initialStartTime, DateFormatEnum.TIME) }}
            </a-select-option>
          </a-select-opt-group>
          <a-select-opt-group v-for="(bookableTimes, key) in groupedBookableTimes" :key="key"
                              :label="makeMomentObject(key, DateFormatEnum.DISPLAY_DATE_WITH_DAY)">
            <a-select-option v-for="(bookableTime, index) in bookableTimes" :key="index"
                             :value="makeMomentObject(`${key} ${bookableTime.startTime}`)">
              {{ bookableTime.startTime }}
            </a-select-option>
          </a-select-opt-group>
        </a-select>
      </template>

      <div v-if="!!appointmentTypeId && !!resourceId" v-for="message in errors.time_start"
           class="validation-error">{{ message }}</div>

    </a-form-model-item>

    <a-form-model-item label="Tot">
      <Skeleton class="skeleton" :loading="true" v-if="loading"></Skeleton>
      <time-picker-component v-else
                             :value="bookableEndTime"
                             :allow-clear="false"
                             :format="timeFormat"
                             :disabled="!appointmentTypeId || !resourceId || (shouldHaveBookableTimes && doesntHaveBookableTimes && !loading)"
                             :disabled-hours="getDisabledHoursEndTime"
                             :disabled-minutes="getDisabledMinutesEndTime"
                             @change="onChangeEndTime($event)"
      />

      <div v-if="!!appointmentTypeId && !!resourceId" v-for="message in errors.time_end"
           class="validation-error">{{ message }}</div>

    </a-form-model-item>
  </span>
</template>

<script>
import bookableTimeApi from "~/api/online_afspraken_bookable_time";
import moment from "moment";
import { Skeleton } from "vue-loading-skeleton";
import DateFormatEnum from "~/enums/DateFormatEnum";
import { isEmptyObject } from "ant-design-vue/lib/vc-form/src/utils";

export default {
  name: "OnlineAfsprakenBookableTimesComponent",
  components: { Skeleton },
  props: {
    startTime: {
      type: String,
      required: false
    },
    endTime: {
      type: String,
      required: false
    },
    appointmentTypeId: {
      type: Number,
      required: false
    },
    resourceId: {
      type: Number,
      required: false
    },
    dateFormat: {
      type: String,
      default: DateFormatEnum.DISPLAY_DATE
    },
    timeFormat: {
      type: String,
      default: DateFormatEnum.TIME
    },
    errors: {
      type: Object,
      default: {}
    }
  },
  data() {
    return {
      DateFormatEnum,
      defaultDuration: null,
      loaded: false,
      loading: false,
      bookableTimes: {},
      bookableStartTime: null,
      bookableEndTime: null,
      initialStartTime: null,
      initialEndTime: null
    };
  },
  mounted() {
    if (this.appointmentTypeId && this.resourceId) {
      this.getBookableTimes(this.startTime || this.makeMomentObject());
    }

    this.initialStartTime = this.makeMomentObject(this.startTime || null);
    this.initialEndTime = this.makeMomentObject(this.endTime || null);
    this.bookableStartTime = this.makeMomentObject(this.startTime || null);
    this.bookableEndTime = this.makeMomentObject(this.endTime || null);
  },
  computed: {
    groupedBookableTimes() {
      if (!this.loading && this.appointmentTypeId && !isEmptyObject(this.bookableTimes)) {
        return this.bookableTimes.reduce(function(rv, x) {
          (rv[x["date"]] = rv[x["date"]] || []).push(x);
          return rv;
        }, {});
      }

      return [];
    },
    shouldHaveBookableTimes() {
      return this.appointmentTypeId && this.resourceId && this.loaded;
    },
    doesntHaveBookableTimes() {
      return this.bookableTimesCount === 0;
    },
    bookableTimesCount() {
      return this.bookableTimes.length;
    }
  },
  methods: {
    makeMomentObject(dateTime = null, format = DateFormatEnum.ISO8601) {
      const momentObject = dateTime ? moment(dateTime) : moment();
      return momentObject.format(format);
    },
    getBookableTimes(startTime = this.makeMomentObject()) {
      this.loading = true;
      this.$emit("loading", true);
      return bookableTimeApi.getByAppointmentTypeAndStartDate(
        this.appointmentTypeId,
        startTime,
        null,
        this.resourceId
      )
        .then((response) => {
          this.bookableTimes = response.data;

          if (typeof this.bookableStartTime !== "undefined" && this.bookableTimesCount > 0) {
            // store the default duration of this specific appointment type
            this.defaultDuration = moment(this.bookableTimes[0].date + " " + this.bookableTimes[0].endTime)
              .diff(moment(this.bookableTimes[0].date + " " + this.bookableTimes[0].startTime));
          }
        })
        .catch(() => {
          this.$notification["error"]({
            message: "Er ging iets mis",
            description: "Het laden van de tijden is niet gelukt."
          });
        })
        .finally(() => {
          if (this.resourceId && this.appointmentTypeId && this.initialStartTime === null) {
            this.findNearestBookableTime();
          }

          this.$emit("loading", false);
          this.loading = false;
          this.loaded = true;
        });
    },
    async onChangeStartDate(date) {
      await this.getBookableTimes(this.makeMomentObject(date));
      this.onChangeStartTime(this.makeMomentObject(date));
    },
    onChangeStartTime(startTime) {
      this.bookableStartTime = startTime;
      this.$emit("changeStartTime", this.bookableStartTime);

      if (startTime === this.initialStartTime) {
        this.bookableEndTime = this.initialEndTime;
        this.$emit("changeEndTime", this.bookableEndTime);
      } else {
        const date = this.makeMomentObject(startTime, DateFormatEnum.DATE);
        const time = this.makeMomentObject(startTime, DateFormatEnum.TIME);
        const bookableTime = this.groupedBookableTimes[date].filter((bookableTime) => bookableTime.startTime === time)[0];
        if (typeof bookableTime === "undefined") {
          this.bookableEndTime = null;
        } else {
          this.bookableEndTime = this.makeMomentObject(bookableTime.date + " " + bookableTime.endTime);
        }
        this.$emit("changeEndTime", this.bookableEndTime);
      }
    },
    onChangeEndTime(time) {
      this.bookableEndTime = this.makeMomentObject(time);
      this.$emit("changeEndTime", this.bookableEndTime);
    },
    getDisabledDate(date) {
      return date < moment()
        .subtract(1, "days");
    },
    getDisabledHoursEndTime() {
      let hours = [];
      for (let i = 0; i < moment(this.bookableStartTime)
        .hour(); i++) {
        hours.push(i);
      }
      return hours;
    },
    getDisabledMinutesEndTime(selectedHour) {
      let minutes = [];
      if (selectedHour === moment(this.bookableStartTime)
        .hour()) {
        for (let i = 0; i <= moment(this.bookableStartTime)
          .minute(); i++) {
          minutes.push(i);
        }
      }
      return minutes;
    },
    findNearestBookableTime() {
      if (typeof this.bookableTimes !== "undefined" && this.bookableTimes.length > 0) {

        const groupedBookableTimes = this.bookableTimes.reduce(function(rv, x) {
          (rv[x["date"]] = rv[x["date"]] || []).push(x);
          return rv;
        }, {});

        const nearestTimes = Object.values(groupedBookableTimes)[0].map((moments) => {
          return moments.date + " " + moments.startTime;
        });

        let nearestTime = null;

        nearestTimes.forEach((time) => {
          let diff = moment(time)
            .diff(moment(this.startTime || null));
          if (diff >= 0 && nearestTime === null) {
            nearestTime = time;
          }
        });

        this.bookableStartTime = this.makeMomentObject(nearestTime);

        // change the end time by default duration
        this.onChangeEndTime(moment(this.bookableStartTime)
          .add(this.defaultDuration));
      }
    }
  },
  watch: {
    appointmentTypeId: function() {
      this.getBookableTimes(this.bookableStartTime);
    },
    resourceId: function() {
      if (this.appointmentTypeId) {
        this.getBookableTimes(this.bookableStartTime);
      }
    }
  }
};
</script>

<style scoped>
.skeleton {
  border-color: rgb(217, 217, 217);
  border-radius: 4px;
  height: 32px;
  width: 100%;
}

.validation-error {
  font-style: italic;
  color: red;
  text-align: right;
}
</style>
